import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import './chat.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone } from '@fortawesome/free-solid-svg-icons';
import SpeechToText from './Mic';
import { Volume2 } from "lucide-react";

function Chat() {
  const location = useLocation();
  const [username] = useState(location.state?.username || '');
  const [fathername] = useState(location.state?.fathername || '');
  const [schoolname] = useState(location.state?.schoolname || '');
  const [userInput, setUserInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [language, setLanguage] = useState('en-US');
  const [isLoading, setIsLoading] = useState(false);
  const [showLogoutWarning, setShowLogoutWarning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const messageEndRef = useRef(null);
  const videoRef = useRef(null);
  const messageContainerRef = useRef(null);
  const inactivityTimeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const [videoElement, setVideoElement] = useState(null);
  const lastMessageRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [names, setNames] = useState([]);
  const [dashUrl, setDashUrl] = useState(null);
  const [videoAllowed, setVideoAllowed] = useState(false);
  const [micError, setMicError] = useState("");
  

  const API = process.env.REACT_APP_API_URL || "https://api.moulirobots.com";

  // const API = process.env.REACT_APP_API_URL || "http://127.0.0.1:1500";


  const axiosInstance = axios.create({
    baseURL: API,
    withCredentials: false, 
    timeout: 3000000,        
  });

  useEffect(() => {
    const fetchChatHistory = async () => {
      if (!username.toLowerCase()) return;
      try {
        setIsLoading(true);
        const response = await axiosInstance.get(`/api/chat/history/${username.toLowerCase()}`);
        if (response.data && Array.isArray(response.data)) {
          const loadedMessages = response.data.map((msg) => ({
            id: msg._id || Date.now() + Math.random(),
            sender: msg.sender,
            text: msg.text,
            content: msg.text 
          }));
          setMessages(loadedMessages);
        }
      } catch (error) {
        console.error("Failed to load chat history:", error);
        setErrorMessage("Could not reload previous chat data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchChatHistory(); 
  }, [username]);

  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const base64ToUint8Array = (base64) => {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const messageContainer = messageContainerRef.current;
    const handleScroll = () => {
      if (isLoading && messageContainer) {
        scrollToBottom();
      }
    };
    if (messageContainer) {
      messageContainer.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (messageContainer) {
        messageContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isLoading]);

  const resetInactivityTimer = () => {
    clearTimeout(inactivityTimeoutRef.current);
    clearTimeout(warningTimeoutRef.current);
    setShowLogoutWarning(false);
    inactivityTimeoutRef.current = setTimeout(() => {
      setShowLogoutWarning(true);
      warningTimeoutRef.current = setTimeout(() => {
        handleLogout();
      }, 100000);
    }, 3000000);
  };

  const updateMessage = (newText, id) => {
    setMessages(prev =>
      prev.map(msg =>
        msg.id === id ? { ...msg, text: newText } : msg
      )
    );
  };

  const typeWriterEffect = async (message, sender, delay = 5) => {
    let displayedMessage = '';
    const messageId = addMessage('', sender);
    for (let i = 0; i < message.length; i++) {
      displayedMessage += message[i];
      updateMessage(displayedMessage, messageId);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  };

  const playAudio = async (base64Data) => {
    if (!base64Data || typeof base64Data !== 'string' || base64Data.trim() === '') return;
    try {
      const audioBlob = new Blob(
        [new Uint8Array(atob(base64Data).split('').map(c => c.charCodeAt(0)))],
        { type: 'audio/mp3' }
      );
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      await new Promise((resolve) => {
        audio.onended = resolve;
        audio.onerror = resolve;
        audio.play().catch(resolve);
      });
    } catch (err) {
      console.error('playAudio error:', err);
    }
  };

  useEffect(() => {
    const handleActivity = () => resetInactivityTimer();
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);
    resetInactivityTimer();
    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      clearTimeout(inactivityTimeoutRef.current);
      clearTimeout(warningTimeoutRef.current);
    };
  }, []);

  const detectLanguage = (message) => {
    if (message.toLowerCase().includes('english')) {
      setLanguage('en');
    }
  };
  const handleMicButtonClick = () => {
    setMicError("");
    setIsListening((prev) => !prev);
  };

  const handleStopListening = () => {
    setIsListening(false);
  };

  const addMessage = (text, sender) => {
    const newMessage = { id: Date.now(), sender, text };
    setMessages(prev => [...prev, newMessage]);
    return newMessage.id;
  };

  const preserveScrollToBottom = () => {
    const container = messageContainerRef.current;
    if (!container) return;
    setTimeout(() => {
      const offsetFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      setTimeout(() => {
        container.scrollTop = container.scrollHeight - container.clientHeight - 1 * offsetFromBottom;
        container.scrollTop = 1.25 * container.scrollTop;
      }, 500);
    }, 0);
  };

  useEffect(() => {
    const timeout = setTimeout(scrollToBottom, 200);
    return () => clearTimeout(timeout);
  }, [videoElement, messages]);

  function formatMessage(rawMessage) {
    const cleanMessage = rawMessage.replace(/\+/g, ' ').trim();
    const match = cleanMessage.match(/^(.*?) ని ఇంగ్లీష్ లో (.*?) అని అంటారు$/);
    if (match) {
      const [, teluguWord, englishWord] = match;
      return `"${teluguWord.trim()}" ని ఇంగ్లీష్ లో "${englishWord.trim()}" అని అంటారు`;
    }
    return cleanMessage;
  }

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      scrollToBottom();
    });
    if (messageContainerRef.current) {
      observer.observe(messageContainerRef.current);
    }
    return () => {
      if (messageContainerRef.current) {
        observer.unobserve(messageContainerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (dashUrl && video) {
      video.src = dashUrl;
      video.load();
      video.play().catch(() => {});
      const handleEnded = () => {
        setDashUrl(null);
        setVideoAllowed(false);
      };
      video.addEventListener('ended', handleEnded);
      return () => {
        if (video) {
          video.removeEventListener('ended', handleEnded);
          video.pause();
          video.src = "";
        }
      };
    }
  }, [dashUrl]);

  const interact = async (input) => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('fathername', fathername);
      formData.append('schoolname', schoolname);
      formData.append('user_input', input);

      const response = await axiosInstance.post(`${API}/api/interact`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const {first_message,
            thard_message,
            audio_data,
            file_data,
            second_message,
            file_data_1,
            After: after,
            video1_audio,
            video_message,
            audio_data_1,
          display_userinput} = response.data;

      function base64ToAudioUrl(base64Data) {
        const audioBlob = new Blob([base64ToUint8Array(base64Data)], { type: 'audio/mp3' });
        return URL.createObjectURL(audioBlob);
      }
     

       if (first_message) {
          const formattedMessage = formatMessage(first_message);
          const new_Message = `AI Teacher: ${formattedMessage}`;

          const newMessage = {
              id: Date.now(),
              sender: 'bot',
              text: new_Message,
              content: new_Message,
          };

          if (file_data && file_data.trim() !== '') {
              newMessage.audioUrl = base64ToAudioUrl(file_data);
          }
          const videoUrl = video1_audio;

          const playSequence = new Promise((resolve) => {

              if (videoUrl && videoUrl.trim() !== '') {

                  const localVideoRef = React.createRef();

                  setVideoElement(
                      <div className="video-container" key={Date.now()}>
                          <video
                              ref={localVideoRef}
                              src={videoUrl}
                              muted
                              loop
                              autoPlay
                              playsInline
                              preload="metadata"
                              className="video-audio"
                              onPlay={() => {
                                  setIsVideoPlaying(true);
                                  preserveScrollToBottom();
                              }}
                              onError={(e) => {
                                  console.error(
                                      "Video playback error:",
                                      e.target.error
                                  );
                              }}
                          />
                      </div>
                  );

                  if (newMessage.audioUrl) {

                      const audio = new Audio(newMessage.audioUrl);

                      audio.addEventListener('ended', () => {

                          const video = localVideoRef.current;

                          if (video) {
                              video.pause();
                              video.currentTime = 0;
                          }

                          setIsVideoPlaying(false);
                          setVideoElement(null);

                          resolve();
                      }, { once: true });

                      setTimeout(() => {

                          audio.play().catch(err => {
                              console.error('Audio play error:', err);
                              setVideoElement(null);
                              setIsVideoPlaying(false);
                              resolve();
                          });

                      }, 300);

                  } else {
                      resolve();
                  }

              } else {
                  resolve();
              }
          });

          setMessages(prev => [...prev, newMessage]);

          await playSequence;
      }

      if (second_message) {
        const formattedMessage = formatMessage(second_message);
        const new_Message = `AI Teacher: ${formattedMessage}`;
        const newMessage = {
          id: Date.now(),
          sender: 'bot',
          text: new_Message,
          content: new_Message,
        };
        if (file_data_1 && file_data_1.trim() !== '') {
          newMessage.audioUrl = base64ToAudioUrl(file_data_1);
        }
        if (newMessage.audioUrl) {
          const audio = new Audio(newMessage.audioUrl);
          setTimeout(() => {
            audio.play().catch(err => console.error('Audio play error:', err));
            audio.addEventListener('ended', () => {
              const video = videoRef.current;
              if (video) {
                video.pause();
                video.currentTime = 0;
              }
              setIsVideoPlaying(false);
              setVideoElement(null);
            });
          }, 300);
        }
        setMessages(prev => [...prev, newMessage]);
      }
      
      if (thard_message) {
        await typeWriterEffect(`Student: ${thard_message}`, 'user');
        await playAudio(audio_data);
      }

      if (response.data.video_message) {
          const videoMessage = response.data.video_message;
          const audioData = response.data.audio_data_1;
          const videoUrl = response.data.video;

          await typeWriterEffect(
              `AI Teacher: ${videoMessage}`,
              'bot'
          );

          await playAudio(audioData);

          console.log("Playing video:", videoUrl);

          if (
              videoUrl &&
              videoUrl.startsWith("https://api.moulirobots.com/videos/")
          ) {
              setDashUrl(videoUrl);
              setVideoAllowed(true);
          } else {
              console.error("Invalid video URL:", videoUrl);
          }
      }


      if (after && after.trim().toLowerCase() === 'ok') {
        setTimeout(() => {
          interact(input);
        }, 3000);
        return;
      }

      

    } catch (error) {
      if (error.code === 'ERR_NETWORK') {
        setErrorMessage(`Cannot reach backend at ${API}`);
      } else if (error.response) {
        setErrorMessage(`Server error ${error.response.status}: ${error.response.data?.message || error.message}`);
      } else if (error.code === 'ECONNABORTED') {
        setErrorMessage('Request timed out. The server took too long to respond.');
      } else {
        setErrorMessage(`Something went wrong: ${error.message}`);
      }
      console.error('Backend error details:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        data: error.response?.data,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (messageToSend) => {
    if (messageToSend.trim() === '') return;
    detectLanguage(messageToSend);
    await interact(messageToSend);
    setUserInput('');
    setTranscript('');
    resetInactivityTimer();
  };


  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      sendMessage(userInput.trim() || transcript.trim());
    }
  };

  useEffect(() => {
    if (!username) {
      alert('No username found!');
      handleLogout();
    }
  }, [username]);

  const handleLogout = () => {
    window.location.href = '/';
  };

  const handlePaste = (e) => {
    e.preventDefault();
  };

  const handleStayLoggedIn = () => {
    setShowLogoutWarning(false);
    clearTimeout(warningTimeoutRef.current);
    resetInactivityTimer();
  };

  const handleChange = (value) => {
    setUserInput(value);
    if (typeof value !== 'string') {
      setSuggestions([]);
      return;
    }
    const lastTerm = value.split(',').pop().trim();
    if (lastTerm === '') {
      setSuggestions([]);
    } else {
      const filtered = names.filter((name) =>
        name.toLowerCase().startsWith(lastTerm.toLowerCase())
      );
      setSuggestions(filtered);
    }
  };

  const handleSuggestionClick = (sug) => {
    const parts = userInput.split(",");
    parts[parts.length - 1] = `${sug}`;
    const newInput = parts.join(",").replace(/\s*,\s*/g, ",");
    setUserInput(newInput);
    setSuggestions([]);
  };

  const speakMessage = (messageText) => {
    const msg = messages.find(m => m.text === messageText);
    if (msg?.audioUrl) {
      const audio = new Audio(msg.audioUrl);
      audio.play().catch(err => console.error('Audio play error:', err));
    } else {
      console.warn("No audio available for this message");
    }
  };


  return (
    <div className="app">
      <div className="Chat">
        <h1 className="user-name">English AI Teacher</h1>
        <button className="Btn" onClick={handleLogout}>
          <div className="sign">
            <svg viewBox="0 0 512 512">
              <path d="M377.9 105.9L500.7 228.7c7.2 7.2 11.3 17.1 11.3 27.3s-4.1 20.1-11.3 27.3L377.9 406.1c-6.4 6.4-15 9.9-24 9.9c-18.7 0-33.9-15.2-33.9-33.9l0-62.1-128 0c-17.7 0-32-14.3-32-32l0-64c0-17.7 14.3-32 32-32l128 0 0-62.1c0-18.7 15.2-33.9 33.9-33.9c9 0 17.6 3.6 24 9.9zM160 96L96 96c-17.7 0-32 14.3-32 32l0 256c0 17.7 14.3 32 32 32l64 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-64 0c-53 0-96-43-96-96L0 128C0 75 43 32 96 32l64 0c17.7 0 32 14.3 32 32s-14.3 32-32 32z" />
            </svg>
          </div>
          <div className="text">Logout</div>
        </button>

        <div className="message-container" ref={messageContainerRef}>
          {messages.map((message, index) => (
            <div
              key={message.id || index}
              ref={index === messages.length - 1 ? lastMessageRef : null}
              className={`message-wrapper ${message.sender}`}>
              <div
                className={`message ${message.sender} ${isVideoPlaying ? "message-narrow" : ""}`}
                contentEditable={false}
                onMouseDown={(e) => e.preventDefault()}
                onCopy={(e) => {
                  e.clipboardData.setData("text/plain", message.text);
                  e.preventDefault();
                }}
                style={{ userSelect: "none" }}>
                {message.sender === 'bot' && (
                  <button
                    onClick={() => speakMessage(message.content || message.text)}
                    className="speak-button">
                    <Volume2 />
                  </button>
                )}
                {message.content || message.text}
              </div>
            </div>
          ))}
          <div ref={messageEndRef} />
          {videoElement}
          {videoAllowed && dashUrl && (
          <div className="video-container2">
              <video
                  key={dashUrl}
                  ref={videoRef}
                  src={dashUrl}
                  playsInline
                  preload="auto"
                  onLoadedMetadata={(e) => {
                      console.log("Duration:", e.target.duration);
                      console.log("Dimensions:",
                          e.target.videoWidth,
                          e.target.videoHeight
                      );
                  }}
                  onError={(e) => {
                      console.error("Video error:", e.target.error);
                      console.error("Video URL:", e.target.currentSrc);
                  }}
                  style={{
                      width: '100%',
                      background: '#000'
                  }}
              />
          </div>
          )}
        </div>

        <div className="input-container">
          <div className="input-wrapper">
            {suggestions.length > 0 && (
              <ul className="suggestions-list">
                {suggestions.map((sug, index) => (
                  <li
                    key={index}
                    onClick={() => {
                      handleSuggestionClick(sug);
                    }}
                  >
                    {sug}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <input
            id="message-input"
            name="message"
            className="input-message"
            type="text"
            value={userInput}
            onChange={(e) => {
              handleChange(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();

                const message =
                  userInput.trim() ||
                  transcript.trim();

                if (message) {
                  sendMessage(message);
                }

                setSuggestions([]);
              }
            }}
            onPaste={handlePaste}
            placeholder={
              isListening
                ? "Listening..."
                : "Ask me..."
            }
            autoComplete="off"
            autoCorrect="on"
            autoCapitalize="sentences"
            spellCheck={true}
          />

          <button
            type="button"
            className="chat_butoon"
            onClick={() => {
              const message =
                userInput.trim() ||
                transcript.trim();

              if (message) {
                sendMessage(message);
              }
            }}
          >
            SEND
          </button>

          <div className="div_gap">

            {micError && (
              <div className="mic-error-message">
                {micError}
              </div>
            )}

          <button
            type="button"
            onClick={handleMicButtonClick}
            className={`mic-button ${
              isListening ? "listening" : ""
            }`}
          >
            <FontAwesomeIcon
              icon={faMicrophone}
            />
          </button>

          <SpeechToText
            isListening={isListening}
            onStopListening={handleStopListening}
            setTranscript={setTranscript}
            setUserInput={setUserInput}
            setMicError={setMicError}
            language={language}
          />

          </div>

        </div>

        {errorMessage && (
          <div className="error-message">{errorMessage}</div>
        )}

        {showLogoutWarning && (
          <div className="logout-warning">
            <div className="warning-content">
              <p>You will be logged out in 10 seconds due to inactivity.</p>
              <button onClick={handleStayLoggedIn}>I am here</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Chat;